import type { CardHandle, ConfirmResult, IStripeSdk } from "./types";

// StripeSdkLocal is the dev/test implementation. It mounts a plain HTML form
// (no Stripe.js) and POSTs the raw card number to the stripe-sim's confirm
// endpoint, which recognises Stripe's canonical test cards (4242…, 4000…0002,
// 4000…9995) and returns a Stripe-shaped PaymentIntent response.
//
// This contract matches StripeSdkReal exactly so swapping is purely a factory
// arg flip — no consumer code changes between local and prod.
export class StripeSdkLocal implements IStripeSdk {
  private cardNumberInput: HTMLInputElement | null = null;
  private container: HTMLElement | null = null;

  constructor(private readonly baseUrl: string) {}

  async mountCardElement(container: HTMLElement): Promise<CardHandle> {
    this.container = container;
    container.innerHTML = "";

    const wrapper = document.createElement("div");
    wrapper.dataset.testid = "stripe-sdk-local-card-form";

    const number = document.createElement("input");
    number.type = "text";
    number.placeholder = "Card number (try 4242424242424242)";
    number.autocomplete = "cc-number";
    number.inputMode = "numeric";
    number.dataset.testid = "stripe-sdk-local-card-number";

    const row = document.createElement("div");
    const expiry = document.createElement("input");
    expiry.type = "text";
    expiry.placeholder = "MM / YY";
    expiry.autocomplete = "cc-exp";
    expiry.dataset.testid = "stripe-sdk-local-card-expiry";

    const cvc = document.createElement("input");
    cvc.type = "text";
    cvc.placeholder = "CVC";
    cvc.autocomplete = "cc-csc";
    cvc.dataset.testid = "stripe-sdk-local-card-cvc";

    row.append(expiry, cvc);
    wrapper.append(number, row);
    container.appendChild(wrapper);

    this.cardNumberInput = number;

    return {
      unmount: () => {
        if (this.container && wrapper.parentElement === this.container) {
          this.container.removeChild(wrapper);
        }
        this.cardNumberInput = null;
        this.container = null;
      },
    };
  }

  async confirmCardPayment(clientSecret: string): Promise<ConfirmResult> {
    if (!this.cardNumberInput) {
      return { status: "failed", message: "Card form not mounted" };
    }
    const paymentMethod = this.cardNumberInput.value.replace(/\s+/g, "");
    if (!paymentMethod) {
      return { status: "failed", message: "Card number is required", code: "incomplete_number" };
    }

    // Extract pi_<id> from client_secret of shape "pi_<id>_secret_<rand>".
    const piId = clientSecret.split("_secret_")[0];
    if (!piId || !piId.startsWith("pi_")) {
      return { status: "failed", message: "Invalid clientSecret", code: "invalid_client_secret" };
    }

    const body = new URLSearchParams({ payment_method: paymentMethod });
    let intent: SimPaymentIntent;
    try {
      const res = await fetch(`${this.baseUrl}/v1/payment_intents/${piId}/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          // The sim accepts any non-empty Bearer; this label is only useful in
          // server logs.
          Authorization: "Bearer sk_test_local",
        },
        body: body.toString(),
      });
      intent = (await res.json()) as SimPaymentIntent;
    } catch (err) {
      return { status: "failed", message: err instanceof Error ? err.message : "Network error" };
    }

    if (intent.status === "succeeded") {
      return { status: "succeeded", paymentIntentId: intent.id };
    }
    const err = intent.last_payment_error;
    return {
      status: "failed",
      message: err?.message ?? "Payment failed",
      code: err?.decline_code ?? err?.code,
    };
  }
}

// Subset of the sim's PaymentIntent shape we read. Field names match Stripe's
// snake_case so this same code works against the real Stripe API verbatim.
interface SimPaymentIntent {
  id: string;
  status: string;
  last_payment_error?: {
    code?: string;
    decline_code?: string;
    message?: string;
  } | null;
}
